import {
  formatDate, FormatWidth, FormStyle, getLocaleDateFormat, getLocaleDayNames,
  getLocaleMonthNames,
  TranslationWidth
} from '@angular/common'
import { Injectable, NgModule, OnDestroy } from '@angular/core'
import {
  NgbAccordionModule, NgbCollapseModule, NgbDateAdapter,
  NgbDateParserFormatter, NgbDatepickerI18n, NgbDatepickerModule, NgbDateStruct, NgbDropdownModule,
  NgbModalModule, NgbNavModule, NgbPopoverModule, NgbTimepickerModule, NgbTooltipModule
} from '@ng-bootstrap/ng-bootstrap'
import { format, getDate, getMonth, getYear, parse } from 'date-fns'
import { Subscription } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'
import { I18nConfigService } from '../../core/services/i18n-config.service'

const modules = [
  NgbDropdownModule,
  NgbCollapseModule,
  NgbPopoverModule,
  NgbModalModule,
  NgbDatepickerModule,
  NgbTimepickerModule,
  NgbTooltipModule,
  NgbNavModule,
  NgbAccordionModule
]

// Translate ngb date picker
// copied from https://github.com/ng-bootstrap/ng-bootstrap/blob/master/src/datepicker/datepicker-i18n.ts, but
// uses I18nConfigService as locale source instead of LOCAL_ID

@Injectable()
export class LocaleAwareNgbDatepickerI18n extends NgbDatepickerI18n implements OnDestroy {
  private weekdaysShort: ReadonlyArray<string>;
  private monthsShort: ReadonlyArray<string>;
  private monthsFull: ReadonlyArray<string>;

  private locale: string;

  // subscriptions
  subscriptions: Subscription[] = [
    this.i18nConfigService.selectedLanguage$.pipe(distinctUntilChanged()).subscribe(locale => this.loadFromLocale(locale))
  ]
  constructor (
    private i18nConfigService: I18nConfigService,
  ) {
    super()
  }

  ngOnDestroy (): void {
    this.subscriptions.forEach(subscription=>subscription.unsubscribe())
  }

  private loadFromLocale (locale: string) {
    this.locale = locale
    const weekdaysStartingOnSunday = getLocaleDayNames(locale, FormStyle.Standalone, TranslationWidth.Short)
    this.weekdaysShort = weekdaysStartingOnSunday.map((day, index) => weekdaysStartingOnSunday[(index + 1) % 7])

    this.monthsShort = getLocaleMonthNames(locale, FormStyle.Standalone, TranslationWidth.Abbreviated)
    this.monthsFull = getLocaleMonthNames(locale, FormStyle.Standalone, TranslationWidth.Wide)
  }

  getWeekdayLabel (weekday: number, width?: Exclude<Intl.DateTimeFormatOptions['weekday'], undefined>): string {
    const translationWidth = width === 'long'
      ? TranslationWidth.Wide
      : width === 'narrow'
        ? TranslationWidth.Narrow
        : TranslationWidth.Short
    const weekdaysStartingOnSunday
        = getLocaleDayNames(this.locale, FormStyle.Standalone, translationWidth)
    const weekdays = weekdaysStartingOnSunday.map((day, index) => weekdaysStartingOnSunday[(index + 1) % 7])
    return weekdays[weekday - 1] || ''
  }

  getWeekdayShortName (weekday: number): string {
    return this.weekdaysShort[weekday - 1]
  }

  getMonthShortName (month: number): string {
    return this.monthsShort[month - 1]
  }

  getMonthFullName (month: number): string {
    return this.monthsFull[month - 1]
  }

  getDayAriaLabel (date: NgbDateStruct): string {
    const jsDate = new Date(date.year, date.month - 1, date.day)
    return formatDate(jsDate, 'fullDate', this.locale)
  }
}

const isDateStringValid = (value: string, dateFormat: string): boolean => {
  try {
    return format(parse(value, dateFormat, Date.now()), dateFormat) === value
  } catch (_) {
    return false
  }
}

const toNgbDate = (date: Date): NgbDateStruct => ({
  year : getYear(date),
  month: getMonth(date) + 1,
  day  : getDate(date)
})

const fromNgbDate = (ngbDate: NgbDateStruct): Date =>
  new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day)

@Injectable()
export class CustomDateAdapter extends NgbDateAdapter<string> implements OnDestroy{
  private dateFormat

  subscriptions: Subscription[] = [
    this.i18nConfigService.selectedLanguage$.subscribe(selectedLanguage => this.dateFormat = getLocaleDateFormat(selectedLanguage, FormatWidth.Short).replace(/Y+/gmi, 'yyyy'))
  ]
  constructor (
    private i18nConfigService: I18nConfigService
  ) {
    super()
  }

  ngOnDestroy (): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe())
  }
  fromModel (value: string | null): NgbDateStruct | null {
    if (value === null) return null
    if(!isDateStringValid(value, this.dateFormat)) return toNgbDate(new Date('Invalid Date'))
    const date = parse(value, this.dateFormat, Date.now())
    return toNgbDate(date)
  }

  toModel (date: NgbDateStruct | null): string | null {
    if (!date) {
      return null
    }
    return format(fromNgbDate(date), this.dateFormat)
  }
}

@Injectable()
export class LocaleAwareNgbDateParserFormatter extends NgbDateParserFormatter implements OnDestroy{
  private dateFormat

  subscriptions: Subscription[] = [
    this.i18nConfigService.selectedLanguage$.subscribe(selectedLanguage => this.dateFormat = getLocaleDateFormat(selectedLanguage, FormatWidth.Short).replace(/Y+/gmi, 'yyyy'))
  ]
  constructor (
    private i18nConfigService: I18nConfigService
  ) {
    super()
  }

  ngOnDestroy (): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe())
  }
  format (date: NgbDateStruct): string {
    if (!date) {
      return ''
    }
    return format(fromNgbDate(date), this.dateFormat)
  }

  parse (value: string): NgbDateStruct | null {
    if(!isDateStringValid(value, this.dateFormat)) {
      return null
    }
    const date = parse(value, this.dateFormat, new Date())
    return toNgbDate(date)
  }
}

export { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap'

@NgModule({
  imports  : modules,
  exports  : modules,
  providers: [
    { provide: NgbDatepickerI18n, useClass: LocaleAwareNgbDatepickerI18n },
  ]
})
export class NgbVendorModule { }
